import { useTranslation } from 'react-i18next';
import {
  FontWeight,
  Heading,
  LiliCheckboxNew,
  LiliTextArea,
  Text,
} from '@lili/lili-frontend-common';
import { ControllerWrapper } from '@Hooks/index';
import { FieldError } from '@Components/inputs/FieldError';
import { EddEmbeddedFields } from '@Pages/happyFlowPages/eddQuestions/pages/embedded/fields.types';
import { NonprofitFundingSourcesOptions } from './hooks/useNonprofitFundingSourcesLogic';

interface NonprofitFundingSourcesFormProps {
  reactiveOptions: string[];
  optionsList: { value: string; getLabel: () => string }[];
}

export const NonprofitFundingSourcesForm = ({
  reactiveOptions = [],
  optionsList,
}: NonprofitFundingSourcesFormProps) => {
  const { t } = useTranslation('eddQuestions');
  const isOtherSelected = reactiveOptions.includes(NonprofitFundingSourcesOptions.OTHER);

  return (
    <div className='flex flex-col gap-[16px]'>
      <Heading.H9>{t('organic.fundingSources.instruction')}</Heading.H9>
      <ControllerWrapper name={EddEmbeddedFields.EDD_NONPROFIT_FUNDING_SOURCES_SELECTED}>
        {({ onChange, error }) => {
          const handleOnChange = (option: string) => {
            if (reactiveOptions.includes(option)) {
              onChange(reactiveOptions.filter((item) => item !== option));
            } else {
              onChange([...reactiveOptions, option]);
            }
          };

          return (
            <div className='flex flex-col gap-[16px] w-full'>
              {optionsList.map((option) => (
                <LiliCheckboxNew
                  key={option.value}
                  id={option.value}
                  checked={reactiveOptions.includes(option.value)}
                  onCheckedChange={() => handleOnChange(option.value)}
                  label={
                    <Text.H9 className='leading-[24px]' fontWeight={FontWeight.MEDIUM}>
                      {option.getLabel()}
                    </Text.H9>
                  }
                  labelAlign='start'
                />
              ))}
              <FieldError
                error={error}
                testId={`${EddEmbeddedFields.EDD_NONPROFIT_FUNDING_SOURCES_SELECTED}-error`}
              />
            </div>
          );
        }}
      </ControllerWrapper>

      {isOtherSelected && (
        <div className='mt-[-10px]'>
          <ControllerWrapper name={EddEmbeddedFields.EDD_NONPROFIT_FUNDING_OTHER} shouldUnregister>
            {({ onChange, value, error, onBlur }) => (
              <LiliTextArea
                value={value}
                onValueChange={onChange}
                errorMsg={error?.message || ''}
                externalErrorMessage={error?.message || ''}
                maxLength={100}
                minHeight='min-h-[88px]'
                placeholder={t('organic.nonprofitFundingSources.otherPlaceholder')}
                onBlur={onBlur}
              />
            )}
          </ControllerWrapper>
        </div>
      )}
    </div>
  );
};
